/**
 * Sleeper-style full draft board.
 *
 * The grid answers "who went where?" while the small need strips above each
 * team answer "what does that roster still lack?". Needs are calculated from
 * the roster and league slots; they are deliberately labelled as such so the
 * view helps the user reason about a positional run without presenting a
 * guessed manager decision as a probability.
 */

import { clear, el } from './dom.ts';
import type { AppState } from '../state/store.ts';
import type { PlayerId, Position } from '../core/types.ts';
import {
  buildDraftBoardCells,
  buildDraftBoardTeams,
  draftSlotForPick,
  type DraftBoardCell,
  type DraftBoardTeam,
} from '../core/draftBoard.ts';
import { opponentDemandForState, renderOpponentDemandSummary } from './opponentDemand.ts';
import type { OpponentDemandForecast } from '../core/opponentDemand.ts';

export interface DraftBoardCallbacks {
  onSelect: (playerId: PlayerId) => void;
  demandOpen?: boolean;
  onDemandToggle?: (open: boolean) => void;
}

export function renderDraftBoard(
  container: HTMLElement,
  state: AppState,
  callbacks: DraftBoardCallbacks,
  opponentDemand: OpponentDemandForecast | null = opponentDemandForState(state),
): void {
  clear(container);

  const { draft, league, universe, draftContext } = state;
  if (!draft || !league || !universe) {
    container.appendChild(el('p', { class: 'empty' }, 'The full draft board is not available yet.'));
    return;
  }

  const teams = Array.from({ length: league.teams }, (_, i) => {
    const draftSlot = i + 1;
    const rosterId = draftContext?.draft.slot_to_roster_id?.[String(draftSlot)] ?? draftSlot;
    return {
      roster_id: rosterId,
      draft_slot: draftSlot,
      team_name: draftContext?.rosterNames.get(rosterId) ?? `Team ${draftSlot}`,
      player_ids: (draft.all_rosters[rosterId] ?? []).filter((playerId) => !state.availabilityOverrides.has(playerId)),
      pick_count: draft.picks.filter((pick) => pick.draft_slot === draftSlot).length,
    };
  });

  const positionOf = (id: PlayerId): Position | null => universe.get(id)?.position ?? null;
  const teamViews = buildDraftBoardTeams(league, teams, positionOf);
  const teamBySlot = new Map(teamViews.map((team) => [team.draft_slot, team]));
  const cells = buildDraftBoardCells(league.rounds, league.teams, league.draft_type, draft.picks);
  const watchSlots = slotsBetweenMyPicks(draft, league.teams, league.draft_type);

  const heading = el(
    'header',
    { class: 'draft-board-title' },
    el('div', {}, el('h2', {}, 'Full draft board'), el('p', { class: 'hint' }, `${league.teams}-team ${league.draft_type} draft · ${league.rounds} rounds`)),
    el('div', { class: 'draft-board-status' }, `Pick #${draft.current_pick_no}`, draft.on_the_clock_slot ? ` · Slot ${draft.on_the_clock_slot} on the clock` : ''),
  );
  container.appendChild(heading);

  container.appendChild(
    el(
      'div',
      { class: 'draft-board-legend', role: 'note' },
      el('span', { class: 'draft-legend-item current' }, 'Current pick'),
      el('span', { class: 'draft-legend-item mine' }, 'Your slot'),
      el('span', { class: 'draft-legend-item watch' }, 'Picks before your next turn'),
      el('span', { class: 'draft-legend-note' }, 'Needs are roster-based, not manager predictions.'),
    ),
  );

  container.appendChild(renderOpponentDemandSummary(opponentDemand, 'full', {
    open: callbacks.demandOpen,
    onToggle: callbacks.onDemandToggle,
  }));

  const grid = el('div', { class: 'draft-grid', role: 'table', 'aria-label': 'Full draft board' });
  grid.style.gridTemplateColumns = `4.25rem repeat(${league.teams}, minmax(10.5rem, 1fr))`;
  grid.style.minWidth = `${4.25 + league.teams * 10.5}rem`;

  grid.appendChild(el('div', { class: 'draft-round-head', role: 'columnheader' }, 'Round'));
  for (const team of teamViews) grid.appendChild(renderTeamHeader(team, draft, watchSlots));

  cells.forEach((row, rowIndex) => {
    const round = rowIndex + 1;
    grid.appendChild(el('div', { class: 'draft-round-label', role: 'rowheader' }, `R${round}`));
    for (const cell of row) {
      const team = teamBySlot.get(cell.draft_slot);
      if (!team) continue;
      grid.appendChild(renderPickCell(cell, team, state, callbacks));
    }
  });

  const gridScroll = el(
    'div',
    {
      class: 'draft-grid-scroll',
      role: 'region',
      tabindex: '0',
      'aria-label': 'Scrollable full draft board',
    },
    grid,
  );
  container.appendChild(gridScroll);
}

function renderTeamHeader(team: DraftBoardTeam, draft: AppState['draft'], watchSlots: Set<number>): HTMLElement {
  if (!draft) return el('div', { class: 'draft-team-header' });

  const isMine = team.draft_slot === draft.my_draft_slot;
  const isWatched = watchSlots.has(team.draft_slot);
  const classes = ['draft-team-header'];
  if (isMine) classes.push('mine');
  if (isWatched) classes.push('watch');
  if (draft.on_the_clock_slot === team.draft_slot) classes.push('current');

  const needBadges = team.needs.slice(0, 3).map((need) =>
    el(
      'span',
      { class: `draft-need pos-${need.position.toLowerCase()}`, title: `${need.open_starters} open ${need.position} starting slot${need.open_starters === 1 ? '' : 's'}` },
      `${need.position} ${need.open_starters}`,
    ),
  );
  if (team.open_flex > 0) {
    needBadges.push(el('span', { class: 'draft-need flex', title: `${team.open_flex} open flex slot${team.open_flex === 1 ? '' : 's'}` }, `FLEX ${team.open_flex}`));
  }
  if (needBadges.length === 0) needBadges.push(el('span', { class: 'draft-need set' }, 'Set'));

  return el(
    'div',
    {
      class: classes.join(' '),
      role: 'columnheader',
      'data-slot': String(team.draft_slot),
      title: `${team.team_name} · roster needs from ${team.roster_size} player${team.roster_size === 1 ? '' : 's'}`,
    },
    el('strong', { class: 'draft-team-name' }, team.team_name),
    el('span', { class: 'draft-team-meta' }, `Slot ${team.draft_slot} · ${team.pick_count} pick${team.pick_count === 1 ? '' : 's'}`),
    el('span', { class: 'draft-team-needs', 'aria-label': 'Roster needs' }, ...needBadges),
    isMine ? el('span', { class: 'draft-team-tag' }, 'You') : null,
    isWatched ? el('span', { class: 'draft-team-tag watch' }, 'Ahead of you') : null,
  );
}

function renderPickCell(
  cell: DraftBoardCell,
  team: DraftBoardTeam,
  state: AppState,
  callbacks: DraftBoardCallbacks,
): HTMLElement {
  const pick = cell.pick;
  const player = pick ? state.universe?.get(pick.player_id) : undefined;
  const current = cell.pick_no === state.draft?.current_pick_no;
  const mine = cell.draft_slot === state.draft?.my_draft_slot;
  const watched = state.draft && state.league
    ? slotsBetweenMyPicks(state.draft, state.league.teams, state.league.draft_type).has(cell.draft_slot)
    : false;
  const classes = ['draft-pick-cell'];
  if (current) classes.push('current');
  if (mine) classes.push('mine');
  if (watched) classes.push('watch');
  if (pick) classes.push('filled');
  if (pick?.manual) classes.push('manual');
  if (pick && state.availabilityOverrides.has(pick.player_id)) classes.push('overridden');

  const node = el(
    'div',
    {
      class: classes.join(' '),
      role: 'cell',
      'data-pick': String(cell.pick_no),
      'data-slot': String(cell.draft_slot),
      title: pick
        ? `${pick.team_name ?? team.team_name} · pick #${cell.pick_no}${state.availabilityOverrides.has(pick.player_id) ? ' · locally marked available' : ''}`
        : current
          ? `Pick #${cell.pick_no} · on the clock`
          : `Pick #${cell.pick_no} · not made yet`,
    },
    el('span', { class: 'draft-pick-number' }, `#${cell.pick_no}`),
  );

  if (player && pick) {
    node.appendChild(
      el(
        'button',
        {
          class: 'draft-pick-player',
          type: 'button',
          onClick: () => callbacks.onSelect(player.player_id_sleeper),
          'aria-label': `View ${player.full_name}`,
        },
        el('span', { class: 'draft-pick-name' }, player.full_name),
        el('span', { class: `draft-pick-meta pos-${(player.position ?? 'QB').toLowerCase()}` }, `${player.position ?? '—'} · ${player.team ?? 'FA'}`),
      ),
    );
  } else if (current) {
    node.appendChild(el('span', { class: 'draft-pick-empty current-label' }, 'On the clock'));
  } else {
    node.appendChild(el('span', { class: 'draft-pick-empty' }, '—'));
  }

  if (pick?.manual) node.appendChild(el('span', { class: 'draft-pick-manual' }, 'manual'));
  if (pick && state.availabilityOverrides.has(pick.player_id)) {
    node.appendChild(el('span', { class: 'draft-pick-override' }, 'available locally'));
  }
  return node;
}

function slotsBetweenMyPicks(
  draft: NonNullable<AppState['draft']>,
  teams: number,
  draftType: string,
): Set<number> {
  const slots = new Set<number>();
  const next = draft.my_next_pick_no;
  if (!next || draft.my_draft_slot === null) return slots;

  // When it is your pick now, compare against the pick after this one. When
  // it is not your turn yet, compare against the next pick you own.
  const target = next === draft.current_pick_no ? draft.my_pick_after_that : next;
  if (!target) return slots;
  const start = next === draft.current_pick_no ? draft.current_pick_no + 1 : draft.current_pick_no;
  for (let pickNo = start; pickNo < target; pickNo++) {
    const slot = draftSlotForPick(pickNo, teams, draftType);
    if (slot !== draft.my_draft_slot) slots.add(slot);
  }
  return slots;
}
